import {
  useMemo,
} from "react";

import {
  useCart,
} from "../../context/CartContext";

import {
  createRetailPOSRuntime,
} from "./retailPOSRuntime";

import {
  createRetailPOSCustomerRuntime,
} from "./retailPOSCustomerRuntime";

import {
  createRetailPOSLoyaltyRuntime,
} from "./retailPOSLoyaltyRuntime";

import {
  createRetailPOSReceiptRuntime,
} from "./retailPOSReceiptRuntime";


/**
 * Retail POS React composition root.
 *
 * Responsibilities:
 *
 * - adapt the authoritative CartContext to RetailPOSRuntime;
 * - compose the stateless Customer Runtime;
 * - compose the stateless Loyalty Runtime;
 * - compose the stateless Receipt Runtime;
 * - inject optional Product capabilities;
 * - inject the existing POSActivitySessionBridge when available.
 *
 * Explicitly excluded:
 *
 * - Cart ownership;
 * - Product ownership;
 * - Customer ownership;
 * - Loyalty ownership;
 * - Receipt ownership;
 * - Payment execution;
 * - business calculations;
 * - persistence;
 * - direct network access.
 */
export function useRetailPOSRuntime({
  productResolver = null,
  productSearchProvider = null,
  sessionBridge = null,

  customerRuntime = null,
  loyaltyRuntime = null,
  receiptRuntime = null,
} = {}) {
  const cart =
    useCart();

  const cartRuntime =
    useMemo(
      () =>
        createRetailPOSCartRuntime(
          cart
        ),
      [
        cart,
      ]
    );

  const resolvedCustomerRuntime =
    useMemo(
      () =>
        customerRuntime
        ?? createRetailPOSCustomerRuntime(),
      [
        customerRuntime,
      ]
    );

  const resolvedLoyaltyRuntime =
    useMemo(
      () =>
        loyaltyRuntime
        ?? createRetailPOSLoyaltyRuntime(),
      [
        loyaltyRuntime,
      ]
    );

  const resolvedReceiptRuntime =
    useMemo(
      () =>
        receiptRuntime
        ?? createRetailPOSReceiptRuntime(),
      [
        receiptRuntime,
      ]
    );

  return useMemo(
    () =>
      createRetailPOSRuntime({
        productResolver,
        productSearchProvider,

        cartRuntime,

        customerRuntime:
          resolvedCustomerRuntime,

        loyaltyRuntime:
          resolvedLoyaltyRuntime,

        sessionBridge,

        receiptRuntime:
          resolvedReceiptRuntime,
      }),
    [
      productResolver,
      productSearchProvider,
      cartRuntime,
      resolvedCustomerRuntime,
      resolvedLoyaltyRuntime,
      sessionBridge,
      resolvedReceiptRuntime,
    ]
  );
}


/**
 * Adapts CartContext to the RetailPOSRuntime Cart contract.
 *
 * The adapter owns no Cart state.
 */
export function createRetailPOSCartRuntime(
  cart
) {
  assertCartContract(
    cart
  );

  return Object.freeze({
    async loadCart() {
      return {
        products:
          Array.isArray(
            cart.items
          )
            ? cart.items
            : [],

        total_ht:
          cart.totals?.total_ht
          ?? 0,

        total_vat:
          cart.totals?.total_vat
          ?? 0,

        total_ttc:
          cart.totals?.total_ttc
          ?? 0,
      };
    },


    async addProduct(
      product,
      quantity = 1,
      context = {}
    ) {
      const productUuid =
        resolveProductUuid(
          product
        );

      await cart.addItem(
        productUuid,
        quantity,
        {
          activityUuid:
            context.activityUuid,

          activityType:
            context.activityType,
        }
      );

      return true;
    },


    async decrementProduct(
      product
    ) {
      const productUuid =
        resolveProductUuid(
          product
        );

      await cart.decrementItem(
        productUuid
      );

      return true;
    },


    async removeProduct(
      product
    ) {
      const productUuid =
        resolveProductUuid(
          product
        );

      await cart.removeItem(
        productUuid
      );

      return true;
    },


    async clearCart() {
      await cart.clearCart();

      return true;
    },


    async checkout(
      payload = {}
    ) {
      const receiptUuid =
        await cart.checkout({
          payments:
            payload.payments
            ?? [],

          customer:
            payload.customer
            ?? null,

          table_number:
            payload.table_number,

          covers:
            payload.covers,
        });

      if (
        typeof receiptUuid
          !== "string"
        || receiptUuid.trim()
          === ""
      ) {
        throw new TypeError(
          "Cart checkout must return a non-empty receipt UUID."
        );
      }

      return Object.freeze({
        salesReceiptUuid:
          receiptUuid.trim(),
      });
    },
  });
}


function resolveProductUuid(
  product
) {
  const raw =
    typeof product
      === "string"
      ? product
      : (
          product?.uuid
          ?? product?.product_uuid
        );

  const uuid =
    typeof raw === "string"
      ? raw.trim()
      : "";

  if (!uuid) {
    throw new TypeError(
      "Retail Cart product must expose a non-empty uuid."
    );
  }

  return uuid;
}


function assertCartContract(
  cart
) {
  if (
    !cart
    || typeof cart !== "object"
  ) {
    throw new TypeError(
      "Retail POS composition requires CartContext."
    );
  }

  [
    "addItem",
    "decrementItem",
    "removeItem",
    "clearCart",
    "checkout",
  ].forEach(
    (method) => {
      if (
        typeof cart[method]
          !== "function"
      ) {
        throw new TypeError(
          `CartContext must expose ${method}().`
        );
      }
    }
  );
}
